/**
 * Matriz de projeção de parcelas de cartão: uma linha por compra parcelada,
 * uma coluna por competência (corrente → última com lançamento), com as linhas
 * agregadas "Demais lançamentos" e "Total do mês" — o total fecha com a fatura
 * quando a visão é um cartão único.
 *
 * Dois consumidores: a aba Projeção da tela de Cartões (visão por cartão /
 * consolidada) e o relatório de Despesas (visão por empresa). A coluna de
 * despesas fica fixa à esquerda enquanto os meses rolam horizontalmente —
 * mesmo padrão sticky da visão anual de Despesas.
 */
import React from 'react';
import clsx from 'clsx';
import type { Company } from '@/types/company';
import type { CreditCardProjection } from '@/types/financial';
import { formatCurrencyBRL } from './financialUtils';
import { monthLabelPtBR } from '@pages/financialReports/financialReportsUtils';
import {
  projectionMonthTotals,
  projectionOthersForView,
  projectionRowsForView,
  projectionViewHasData,
  type ProjectionView,
} from './creditCardProjection';

/** Coluna de despesa fixa à esquerda no scroll horizontal (bg por célula). */
const stickyCellCls = 'sticky left-0 z-10 py-2 px-3 min-w-[150px] sm:min-w-[260px]';

const MoneyCell: React.FC<{ value: number; label?: string; bold?: boolean }> = ({ value, label, bold }) => {
  if (value === 0 && !label) {
    return <td className="text-right py-2 px-3 text-slate-300">–</td>;
  }
  return (
    <td className={clsx('text-right py-2 px-3 whitespace-nowrap', bold && 'font-semibold', value < 0 ? 'text-red-600' : 'text-emerald-600')}>
      {formatCurrencyBRL(value)}
      {label && <span className="block text-[10px] font-normal text-slate-400">{label}</span>}
    </td>
  );
};

export const CreditCardProjectionMatrix: React.FC<{
  data: CreditCardProjection;
  companies: Company[];
  view: ProjectionView;
}> = ({ data, companies, view }) => {
  if (data.months.length === 0 || !projectionViewHasData(data, view)) {
    return (
      <p className="text-sm text-slate-500 py-6 text-center">
        Nenhuma despesa de cartão registrada para as próximas competências.
      </p>
    );
  }

  const rows = projectionRowsForView(data, view);
  const others = projectionOthersForView(data, view);
  const totals = projectionMonthTotals(data, view);
  const hasOthers = others.some(v => v !== 0);

  const companyName = (companyId: number | null): string | null => {
    if (companyId === null) return null;
    return companies.find(c => c.company_id === companyId)?.name ?? `Empresa ${companyId}`;
  };
  const cardLabel = (cardId: number): string =>
    data.cards.find(c => c.idCreditCards === cardId)?.description ?? `Cartão ${cardId}`;

  // Sub-rótulo da linha: no consolidado por cartão mostra o cartão; na visão
  // por empresa (relatório) mostra a empresa — a dimensão que distingue.
  const rowSubLabel = (companyId: number | null, cardId: number): string | null => {
    if (view.kind === 'card') {
      return view.cardId === null ? cardLabel(cardId) : null;
    }
    return view.companyId === null ? companyName(companyId) : null;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className={clsx(stickyCellCls, 'bg-white text-left font-semibold text-slate-600')}>Despesa</th>
            {data.months.map(m => (
              <th key={m} className="text-right py-2 px-3 font-semibold text-slate-600 whitespace-nowrap">
                {monthLabelPtBR(m)}
              </th>
            ))}
            <th className="text-right py-2 px-3 font-bold text-slate-700 whitespace-nowrap">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(g => {
            const sub = rowSubLabel(g.companyId, g.idCreditCards);
            return (
              <tr key={g.key} className="border-b border-slate-100 hover:bg-slate-50">
                <td className={clsx(stickyCellCls, 'bg-white')}>
                  <span className="text-slate-800">{g.description}</span>
                  {g.recurring ? (
                    <span className="ml-2 inline-block rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
                      recorrente
                    </span>
                  ) : (
                    g.repeatTotal != null &&
                    g.repeatTotal > 1 && <span className="ml-2 text-xs text-slate-400">{g.repeatTotal}x</span>
                  )}
                  {sub && <span className="block text-xs text-slate-400">{sub}</span>}
                </td>
                {g.monthAmounts.map((v, i) => (
                  <MoneyCell key={data.months[i]} value={v} label={g.monthLabels[i] || undefined} />
                ))}
                <MoneyCell value={g.total} bold />
              </tr>
            );
          })}
          {hasOthers && (
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <td className={clsx(stickyCellCls, 'bg-slate-50 text-slate-500')}>
                Demais lançamentos
                <span className="block text-xs text-slate-400">avulsos (sem parcelas nem recorrências)</span>
              </td>
              {others.map((v, i) => (
                <MoneyCell key={data.months[i]} value={v} />
              ))}
              <MoneyCell value={others.reduce((a, b) => a + b, 0)} bold />
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold border-t border-slate-200">
            <td className={clsx(stickyCellCls, 'bg-slate-50 text-slate-700')}>Total do mês</td>
            {totals.map((v, i) => (
              <MoneyCell key={data.months[i]} value={v} bold />
            ))}
            <MoneyCell value={totals.reduce((a, b) => a + b, 0)} bold />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default CreditCardProjectionMatrix;
